import { theme } from "@/app/theme";

/** How custom (light/dark) icons resolve against the app theme. Material glyphs ignore this. */
export type IconTheming = "Themed" | "Inverse" | "OnLight" | "OnDark";

const materialIconMap = {
  AccessTime: "access_time",
  Add: "add",
  AddCircle: "add_circle",
  AddCircleOutlined: "add_circle_outlined",
  AddPhotoAlternate: "add_photo_alternate",
  ArrowBack: "arrow_back",
  ArrowBackIos: "arrow_back_ios",
  ArrowDownward: "arrow_downward",
  ArrowForward: "arrow_forward",
  ArrowUpward: "arrow_upward",
  Assignment: "assignment",
  AttachFile: "attach_file",
  Block: "block",
  BugReport: "bug_report",
  Build: "build",
  CalendarMonth: "calendar_month",
  CalendarToday: "calendar_today",
  Check: "check",
  CheckBox: "check_box",
  CheckBoxOutlineBlank: "check_box_outline_blank",
  CheckCircle: "check_circle",
  CheckCircleOutline: "check_circle_outline",
  ChevronLeft: "chevron_left",
  ChevronRight: "chevron_right",
  Clear: "clear",
  Close: "close",
  Colorize: "colorize",
  Construction: "construction",
  ContactSupport: "contact_support",
  Contacts: "contacts",
  Create: "create",
  CropFree: "crop_free",
  DateRange: "date_range",
  Delete: "delete",
  Description: "description",
  DirectionsCar: "directions_car",
  DoNotDisturbOn: "do_not_disturb_on",
  Done: "done",
  DragIndicator: "drag_indicator",
  Edit: "edit",
  Error: "error",
  Event: "event",
  ExpandLess: "expand_less",
  ExpandMore: "expand_more",
  Favorite: "favorite",
  FileDownload: "file_download",
  FileUpload: "file_upload",
  FilterList: "filter_list",
  FormatListBulleted: "format_list_bulleted",
  Fullscreen: "fullscreen",
  FullscreenExit: "fullscreen_exit",
  GpsFixed: "gps_fixed",
  HighlightOff: "highlight_off",
  Home: "home",
  IndeterminateCheckBox: "indeterminate_check_box",
  Info: "info",
  Keyboard: "keyboard",
  KeyboardArrowDown: "keyboard_arrow_down",
  KeyboardArrowUp: "keyboard_arrow_up",
  Launch: "launch",
  LightbulbOutline: "lightbulb_outline",
  Link: "link",
  List: "list",
  LocalBar: "local_bar",
  LocalDrink: "local_drink",
  LocalShipping: "local_shipping",
  Lock: "lock",
  LockOpen: "lock_open",
  Map: "map",
  MenuBook: "menu_book",
  MonetizationOn: "monetization_on",
  MoreVert: "more_vert",
  OfflinePin: "offline_pin",
  OpenInNew: "open_in_new",
  PhotoCamera: "photo_camera",
  QrCodeScanner: "qr_code_scanner",
  Search: "search",
  SentimentNeutral: "sentiment_neutral",
  SentimentVeryDissatisfied: "sentiment_very_dissatisfied",
  SentimentVerySatisfied: "sentiment_very_satisfied",
  Settings: "settings",
  Share: "share",
  ShoppingBasket: "shopping_basket",
  SignalWifiOff: "signal_wifi_off",
  Smartphone: "smartphone",
  Star: "star",
  SupportAgent: "support_agent",
  VerticalAlignBottom: "vertical_align_bottom",
  ViewColumn: "view_column",
  Visibility: "visibility",
  VisibilityOff: "visibility_off",
  Warning: "warning",
  WarningAmber: "warning_amber",
  Web: "web",
} as const;

/** Custom CSS icons from `_icons.sass` / former `vuetify` icons.values registry. */
const customIconMap = {
  Accept: {
    light: "custom-icon accept",
    dark: "custom-icon accept-dark",
  },
  Cancel: {
    light: "custom-icon cancel",
    dark: "custom-icon cancel-dark",
  },
  Phone: {
    light: "custom-icon phone",
    dark: "custom-icon phone-dark",
  },
  Customise: {
    light: "custom-icon customise",
    dark: "custom-icon customise-dark",
  },
  Van: {
    light: "custom-icon van",
    dark: "custom-icon van",
  },
  ChristmasHunt: {
    light: "custom-icon christmas-hunt-black",
    dark: "custom-icon christmas-hunt-white",
  },
} as const;

export type BmIconName =
  | keyof typeof materialIconMap
  | keyof typeof customIconMap;

function useDarkVariant(theming: IconTheming): boolean {
  const isDark = theme.isDark();
  switch (theming) {
    case "Themed":
      return isDark;
    case "Inverse":
      return !isDark;
    case "OnLight":
      return false;
    case "OnDark":
      return true;
  }
}

/**
 * Resolve a stable Bm icon name to the string passed into Vuetify `<v-icon>`
 * (Material glyph or custom CSS class list).
 */
export function resolveIcon(
  name: BmIconName,
  theming: IconTheming = "Themed"
): string {
  if (name in materialIconMap) {
    return materialIconMap[name as keyof typeof materialIconMap];
  }

  const custom = customIconMap[name as keyof typeof customIconMap];
  return useDarkVariant(theming) ? custom.dark : custom.light;
}

/** Alias for use on Vuetify string icon props (`prepend-icon`, `append-icon`, …). */
export function bmIconName(
  name: BmIconName,
  theming: IconTheming = "Themed"
): string {
  return resolveIcon(name, theming);
}
